"use client"

import * as React from 'react'
import { z } from 'zod'
import { useRouter } from 'next/navigation'
import { Page, PageBody } from '@open-mercato/ui/backend/Page'
import { CrudForm, type CrudField, type CrudFormGroup } from '@open-mercato/ui/backend/CrudForm'
import { createCrud } from '@open-mercato/ui/backend/utils/crud'
import { readApiResultOrThrow } from '@open-mercato/ui/backend/utils/apiCall'
import { flash } from '@open-mercato/ui/backend/FlashMessages'
import { useT } from '@open-mercato/shared/lib/i18n/context'
import { useOrganizationScopeDetail } from '@open-mercato/shared/lib/frontend/useOrganizationScope'
import {
  CimWaybillType,
  ChargesPaidBy,
  RidPackingGroup,
} from '../../../../../shared/enums'

// Client-safe form schema — omits organizationId/tenantId (injected server-side)
// Imports enums from shared/enums directly to avoid pulling in @mikro-orm/core
const cimWaybillFormSchema = z.object({
  waybillNumber: z.string().min(1).max(50),
  waybillType: z.nativeEnum(CimWaybillType).optional(),
  documentId: z.string().uuid().optional().nullable().or(z.literal('')),
  trainPlanId: z.string().uuid().optional().nullable().or(z.literal('')),
  consignorName: z.string().min(1).max(200),
  consigneeName: z.string().min(1).max(200),
  carrierName: z.string().min(1).max(200),
  forwardingAgentName: z.string().max(200).optional().nullable(),
  stationOfDepartureId: z.string().uuid(),
  stationOfDestinationId: z.string().uuid(),
  goodsDescription: z.string().min(1).max(2000),
  nhmCode: z.string().max(10).optional().nullable(),
  hsCode: z.string().max(10).optional().nullable(),
  grossWeightKg: z.coerce.number().min(0),
  numberOfPackages: z.coerce.number().int().min(0).optional().nullable(),
  packageType: z.string().max(50).optional().nullable(),
  marksAndNumbers: z.string().max(500).optional().nullable(),
  isDangerous: z.boolean().optional(),
  ridClass: z.string().max(10).optional().nullable(),
  ridUnNumber: z.string().max(10).optional().nullable(),
  ridPackingGroup: z.nativeEnum(RidPackingGroup).optional().nullable(),
  ridProperShippingName: z.string().max(500).optional().nullable(),
  freightCharges: z.coerce.number().optional().nullable(),
  freightCurrency: z.string().length(3).optional().or(z.literal('')),
  chargesPaidBy: z.nativeEnum(ChargesPaidBy).optional(),
  remarks: z.string().max(2000).optional().nullable(),
})

export default function CreateCimWaybillPage() {
  const t = useT()
  const router = useRouter()
  const { organizationId, tenantId } = useOrganizationScopeDetail()

  const formSchema = React.useMemo(() => cimWaybillFormSchema, [])

  const fields = React.useMemo<CrudField[]>(() => [
    // identification
    {
      id: 'waybillNumber',
      label: t('cim.fields.waybillNumber', 'Waybill Number'),
      type: 'text',
      required: true,
      placeholder: 'CIM-2026-00001',
    },
    {
      id: 'waybillType',
      label: t('cim.fields.waybillType', 'Waybill Type'),
      type: 'select',
      options: [
        { value: 'CIM', label: 'CIM' },
        { value: 'CIM_SMGS', label: 'CIM/SMGS' },
        { value: 'ECIM', label: 'eCIM' },
      ],
    },
    {
      id: 'documentId',
      label: t('cim.fields.documentId', 'Transport Document ID'),
      type: 'combobox',
      placeholder: t('cim.waybills.placeholders.searchDocument', 'Search transport documents...'),
      loadOptions: async (query?: string) => {
        const params = new URLSearchParams({ pageSize: '20' })
        if (query?.trim()) params.set('search', query.trim())
        const data = await readApiResultOrThrow<{ items?: Array<{ id: string; documentNumber: string; documentType: string }> }>(
          `/api/freight/documents?${params.toString()}`,
        )
        return (data.items ?? []).map((doc) => ({
          value: doc.id,
          label: `${doc.documentNumber} (${doc.documentType})`,
        }))
      },
      allowCustomValues: false,
    },
    {
      id: 'trainPlanId',
      label: t('cim.fields.trainPlanId', 'Train Plan ID'),
      type: 'combobox',
      placeholder: t('cim.waybills.placeholders.searchTrainPlan', 'Search train plans...'),
      loadOptions: async (query?: string) => {
        const params = new URLSearchParams({ pageSize: '20' })
        if (query?.trim()) params.set('search', query.trim())
        const data = await readApiResultOrThrow<{ items?: Array<{ id: string; planNumber: string; name?: string | null }> }>(
          `/api/train_planning/train-plans?${params.toString()}`,
        )
        return (data.items ?? []).map((plan) => ({
          value: plan.id,
          label: plan.name ? `${plan.planNumber} — ${plan.name}` : plan.planNumber,
        }))
      },
      allowCustomValues: false,
    },
    // parties
    {
      id: 'consignorName',
      label: t('cim.fields.consignorName', 'Consignor Name'),
      type: 'text',
      required: true,
      placeholder: 'PKP Cargo S.A.',
    },
    {
      id: 'consigneeName',
      label: t('cim.fields.consigneeName', 'Consignee Name'),
      type: 'text',
      required: true,
      placeholder: 'DB Cargo AG',
    },
    {
      id: 'carrierName',
      label: t('cim.fields.carrierName', 'Carrier Name'),
      type: 'text',
      required: true,
      placeholder: 'PKP Cargo S.A.',
    },
    {
      id: 'forwardingAgentName',
      label: t('cim.fields.forwardingAgentName', 'Forwarding Agent'),
      type: 'text',
      placeholder: 'Optional',
    },
    // route
    {
      id: 'stationOfDepartureId',
      label: t('cim.fields.stationOfDepartureId', 'Station of Departure'),
      type: 'combobox',
      required: true,
      placeholder: t('cim.waybills.placeholders.searchStation', 'Search stations...'),
      loadOptions: async (query?: string) => {
        const params = new URLSearchParams({ pageSize: '20' })
        if (query?.trim()) params.set('search', query.trim())
        const data = await readApiResultOrThrow<{ items?: Array<{ id: string; name: string; code: string }> }>(
          `/api/rail_network/stations?${params.toString()}`,
        )
        return (data.items ?? []).map((station) => ({
          value: station.id,
          label: `${station.name} (${station.code})`,
        }))
      },
      allowCustomValues: false,
    },
    {
      id: 'stationOfDestinationId',
      label: t('cim.fields.stationOfDestinationId', 'Station of Destination'),
      type: 'combobox',
      required: true,
      placeholder: t('cim.waybills.placeholders.searchStation', 'Search stations...'),
      loadOptions: async (query?: string) => {
        const params = new URLSearchParams({ pageSize: '20' })
        if (query?.trim()) params.set('search', query.trim())
        const data = await readApiResultOrThrow<{ items?: Array<{ id: string; name: string; code: string }> }>(
          `/api/rail_network/stations?${params.toString()}`,
        )
        return (data.items ?? []).map((station) => ({
          value: station.id,
          label: `${station.name} (${station.code})`,
        }))
      },
      allowCustomValues: false,
    },
    // goods
    {
      id: 'goodsDescription',
      label: t('cim.fields.goodsDescription', 'Goods Description'),
      type: 'textarea',
      required: true,
      placeholder: 'Description of goods being transported',
    },
    {
      id: 'nhmCode',
      label: t('cim.fields.nhmCode', 'NHM Code'),
      type: 'text',
      placeholder: '01 11 00',
    },
    {
      id: 'hsCode',
      label: t('cim.fields.hsCode', 'HS Code'),
      type: 'text',
      placeholder: '100190',
    },
    {
      id: 'grossWeightKg',
      label: t('cim.fields.grossWeightKg', 'Gross Weight (kg)'),
      type: 'number',
      required: true,
      placeholder: '0',
    },
    {
      id: 'numberOfPackages',
      label: t('cim.fields.numberOfPackages', 'Number of Packages'),
      type: 'number',
      placeholder: '0',
    },
    {
      id: 'packageType',
      label: t('cim.fields.packageType', 'Package Type'),
      type: 'text',
      placeholder: 'e.g. pallets, bags',
    },
    {
      id: 'marksAndNumbers',
      label: t('cim.fields.marksAndNumbers', 'Marks & Numbers'),
      type: 'text',
      placeholder: 'Optional marks and numbers',
    },
    // rid
    {
      id: 'isDangerous',
      label: t('cim.fields.isDangerous', 'Dangerous Goods'),
      type: 'checkbox',
    },
    {
      id: 'ridClass',
      label: t('cim.fields.ridClass', 'RID Class'),
      type: 'text',
      placeholder: 'e.g. 3',
    },
    {
      id: 'ridUnNumber',
      label: t('cim.fields.ridUnNumber', 'RID UN Number'),
      type: 'text',
      placeholder: 'e.g. UN1203',
    },
    {
      id: 'ridPackingGroup',
      label: t('cim.fields.ridPackingGroup', 'RID Packing Group'),
      type: 'select',
      options: [
        { value: '', label: '—' },
        { value: 'I', label: 'I' },
        { value: 'II', label: 'II' },
        { value: 'III', label: 'III' },
      ],
    },
    {
      id: 'ridProperShippingName',
      label: t('cim.fields.ridProperShippingName', 'RID Proper Shipping Name'),
      type: 'text',
      placeholder: 'Proper shipping name per RID',
    },
    // financial
    {
      id: 'freightCharges',
      label: t('cim.fields.freightCharges', 'Freight Charges'),
      type: 'number',
      placeholder: '0.00',
    },
    {
      id: 'freightCurrency',
      label: t('cim.fields.freightCurrency', 'Currency'),
      type: 'text',
      placeholder: 'PLN',
    },
    {
      id: 'chargesPaidBy',
      label: t('cim.fields.chargesPaidBy', 'Charges Paid By'),
      type: 'select',
      options: [
        { value: 'CONSIGNOR', label: 'Consignor' },
        { value: 'CONSIGNEE', label: 'Consignee' },
        { value: 'THIRD_PARTY', label: 'Third Party' },
      ],
    },
    // other
    {
      id: 'remarks',
      label: t('cim.fields.remarks', 'Remarks'),
      type: 'textarea',
      placeholder: 'Optional remarks',
    },
  ], [t])

  const groups = React.useMemo<CrudFormGroup[]>(() => [
    {
      id: 'identification',
      title: t('cim.groups.identification', 'Identification'),
      fields: ['waybillNumber', 'waybillType', 'documentId', 'trainPlanId'],
    },
    {
      id: 'parties',
      title: t('cim.groups.parties', 'Parties'),
      fields: ['consignorName', 'consigneeName', 'carrierName', 'forwardingAgentName'],
    },
    {
      id: 'route',
      title: t('cim.groups.route', 'Route'),
      fields: ['stationOfDepartureId', 'stationOfDestinationId'],
    },
    {
      id: 'goods',
      title: t('cim.groups.goods', 'Goods Description'),
      fields: ['goodsDescription', 'nhmCode', 'hsCode', 'grossWeightKg', 'numberOfPackages', 'packageType', 'marksAndNumbers'],
    },
    {
      id: 'rid',
      title: t('cim.groups.rid', 'RID Dangerous Goods'),
      fields: ['isDangerous', 'ridClass', 'ridUnNumber', 'ridPackingGroup', 'ridProperShippingName'],
    },
    {
      id: 'financial',
      title: t('cim.groups.financial', 'Financial'),
      fields: ['freightCharges', 'freightCurrency', 'chargesPaidBy'],
    },
    {
      id: 'other',
      title: t('cim.groups.other', 'Other'),
      fields: ['remarks'],
    },
  ], [t])

  return (
    <Page>
      <PageBody>
        <CrudForm
          title={t('cim.create.title', 'New CIM Waybill')}
          backHref="/backend/cim-waybills"
          fields={fields}
          groups={groups}
          submitLabel={t('cim.waybills.actions.create', 'New Waybill')}
          cancelHref="/backend/cim-waybills"
          schema={formSchema}
          onSubmit={async (values) => {
            try {
              const payload = {
                ...values,
                organizationId,
                tenantId,
              }
              const { result: created } = await createCrud<{ id?: string }>('cim/waybills', payload)
              flash(t('cim.waybills.flash.created', 'CIM waybill created'), 'success')
              router.push(`/backend/cim-waybills/${created?.id}`)
            } catch (err) {
              throw err
            }
          }}
        />
      </PageBody>
    </Page>
  )
}
